import moment from "moment";

export const WEEK_DAYS = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

export const DAY_LABELS = {
  MO: "Monday",
  TU: "Tuesday",
  WE: "Wednesday",
  TH: "Thursday",
  FR: "Friday",
  SA: "Saturday",
  SU: "Sunday",
};

export const DEFAULT_DAY_INTERVALS = [{ from: "07:00", to: "18:00" }];

export const getDefaultOpeningHours = () => ({
  same_every_day: false,
  schedule: {
    MO: { active: true, intervals: [{ from: "07:00", to: "18:00" }] },
    TU: { active: true, intervals: [{ from: "07:00", to: "18:00" }] },
    WE: { active: true, intervals: [{ from: "07:00", to: "18:00" }] },
    TH: { active: true, intervals: [{ from: "07:00", to: "18:00" }] },
    FR: { active: true, intervals: [{ from: "07:00", to: "18:00" }] },
    SA: { active: true, intervals: [{ from: "07:00", to: "14:00" }] },
    SU: { active: false, intervals: [{ from: "07:00", to: "18:00" }] },
  },
});

/**
 * Normalizes opening hours data from whatever format (object, stringified JSON, null)
 * into a standard format.
 */
export const normalizeOpeningHours = (data) => {
  if (!data) return getDefaultOpeningHours();

  let parsed = data;
  if (typeof data === "string") {
    try {
      parsed = JSON.parse(data);
    } catch (e) {
      return getDefaultOpeningHours();
    }
  }

  if (typeof parsed !== "object" || parsed === null) {
    return getDefaultOpeningHours();
  }

  const defaultHours = getDefaultOpeningHours();
  const schedule = {};

  WEEK_DAYS.forEach((day) => {
    const rawDay =
      (parsed.dayKey === day ? parsed : null) ||
      parsed.schedule?.[day] ||
      parsed[day] ||
      (Array.isArray(parsed) ? parsed.find((d) => d?.day === day || d?.dayKey === day) : null);
    if (rawDay) {
      const active = rawDay.active !== undefined ? Boolean(rawDay.active) : true;
      let intervals = [];
      if (Array.isArray(rawDay.intervals)) {
        intervals = rawDay.intervals
          .map((i) => ({
            from: i?.from || "07:00",
            to: i?.to || (day === "SA" ? "14:00" : "18:00"),
          }))
          .filter(Boolean);
      }
      if (!intervals.length) {
        intervals = [{ from: "07:00", to: day === "SA" ? "14:00" : "18:00" }];
      }
      schedule[day] = { active, intervals };
    } else {
      schedule[day] = defaultHours.schedule[day];
    }
  });

  return {
    same_every_day: Boolean(parsed.same_every_day),
    schedule,
  };
};

/**
 * Maps a Date/Moment to a 2-letter day key ('MO', 'TU', etc.)
 */
export const getDayKeyFromDate = (date) => {
  if (!date) return "MO";
  const m = moment(date);
  if (!m.isValid()) return "MO";

  // Moment: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
  const dayIndex = m.day();
  const map = {
    0: "SU",
    1: "MO",
    2: "TU",
    3: "WE",
    4: "TH",
    5: "FR",
    6: "SA",
  };
  return map[dayIndex] || "MO";
};

/**
 * Gets single day timing info from opening hours structure
 */
export const getDayOpeningInfo = (openingHours, dateOrDayKey) => {
  const normalized = normalizeOpeningHours(openingHours);
  const dayKey = typeof dateOrDayKey === "string" && WEEK_DAYS.includes(dateOrDayKey.toUpperCase())
    ? dateOrDayKey.toUpperCase()
    : getDayKeyFromDate(dateOrDayKey);

  return {
    dayKey,
    dayLabel: DAY_LABELS[dayKey] || dayKey,
    ...(normalized.schedule[dayKey] || { active: true, intervals: [{ from: "07:00", to: dayKey === "SA" ? "14:00" : "18:00" }] }),
  };
};

/**
 * Converts a "HH:mm" time string into minutes from midnight.
 */
const NAMED_TIMING_HOURS = {
  MORNING: "08:00",
  "BEFORE NOON": "10:00",
  BEFORE_NOON: "10:00",
  MIDDAY: "12:00",
  NOON: "12:00",
  AFTERNOON: "14:00",
  EVENING: "18:00",
  NIGHT: "22:00",
};

export const timeToMinutes = (timeStr) => {
  if (!timeStr || typeof timeStr !== "string") return null;
  const trimmed = timeStr.trim();
  const upper = trimmed.toUpperCase().replace(/\s+/g, " ");
  if (NAMED_TIMING_HOURS[upper]) {
    const [h, m] = NAMED_TIMING_HOURS[upper].split(":").map(Number);
    return h * 60 + m;
  }
  const match = trimmed.match(/^(\d{1,2}):(\d{2})/);
  if (!match) return null;
  const h = parseInt(match[1], 10);
  const m = parseInt(match[2], 10);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
};

/**
 * Parses departure time and computes expected delivery window [startMin, endMin].
 */
export const parseDeliveryWindow = (departureTime, durationHours = 3) => {
  const startMin = timeToMinutes(departureTime);
  if (startMin === null) return null;
  const endMin = startMin + durationHours * 60;
  return { startMin, endMin };
};

/**
 * Checks if a customer has a deposit agreement on file (free text, uploaded files, or legacy).
 */
export const hasCustomerDepositAgreement = (customer) => {
  if (!customer) return false;

  const depositText = customer.deposit_agreement_text || customer.deposit_agreement;
  if (typeof depositText === "string") {
    const trimmed = depositText.trim();
    if (trimmed && trimmed.toUpperCase() !== "NONE") {
      return true;
    }
  }

  if (Array.isArray(customer.deposit_agreement_files) && customer.deposit_agreement_files.length > 0) {
    return true;
  }

  if (typeof customer.deposit_agreement_files === "string") {
    try {
      const parsed = JSON.parse(customer.deposit_agreement_files);
      if (Array.isArray(parsed) && parsed.length > 0) return true;
    } catch {
      // not json
    }
  }

  if (customer.deposit_agreement_file_url || customer.deposit_agreement_attachment_url) {
    return true;
  }

  return false;
};

/**
 * Validates whether delivery falls outside customer opening hours.
 */
export const isOrderOutsideCustomerOpeningHours = (customerOrTiming, departureDate, departureTime) => {
  const openingTiming =
    customerOrTiming?.opening_timing ||
    customerOrTiming?.opening_hours ||
    customerOrTiming?.opening_timings ||
    customerOrTiming;

  const normalized = normalizeOpeningHours(openingTiming);
  const dayKey = getDayKeyFromDate(departureDate);
  const dayConfig = normalized.schedule?.[dayKey];

  if (!dayConfig || !dayConfig.active) {
    return true; // Customer is closed on this day
  }

  const intervals = dayConfig.intervals || [];
  if (!intervals.length) {
    return true;
  }

  const window = parseDeliveryWindow(departureTime, 3);
  if (!window) {
    return false;
  }

  // Check if any interval completely covers [startMin, endMin]
  for (const interval of intervals) {
    const iFromMin = timeToMinutes(interval.from);
    const iToMin = timeToMinutes(interval.to);
    if (iFromMin !== null && iToMin !== null) {
      if (iFromMin <= window.startMin && iToMin >= window.endMin) {
        return false; // Covered
      }
    }
  }

  return true; // Outside all intervals
};

/**
 * Checks if an order needs a deposit agreement but doesn't have one.
 */
export const isOrderMissingDepositAgreement = (orderOrCustomer, departureDate, departureTime) => {
  const customer = orderOrCustomer?.Customer || orderOrCustomer?.customer || orderOrCustomer;
  if (!customer) return false;

  if (hasCustomerDepositAgreement(customer)) {
    return false;
  }

  const dateToUse = departureDate || orderOrCustomer?.departure_date || orderOrCustomer?.date;
  const timeToUse = departureTime || orderOrCustomer?.departure || orderOrCustomer?.delivery_timing;
  const timingToUse = customer;

  return isOrderOutsideCustomerOpeningHours(timingToUse, dateToUse, timeToUse);
};

/**
 * Validates a single interval.
 * Returns an error string or null if valid.
 */
export const validateInterval = (interval) => {
  if (!interval || !interval.from || !interval.to) {
    return "Time is required";
  }
  const fromMin = timeToMinutes(interval.from);
  const toMin = timeToMinutes(interval.to);

  if (fromMin === null || toMin === null) {
    return "Invalid time format";
  }
  if (fromMin >= toMin) {
    return "Start time must be before end time";
  }
  return null;
};

/**
 * Validates a list of intervals for a day.
 * Returns an array of error messages corresponding to each interval (null if no error).
 */
export const validateDayIntervals = (intervals) => {
  if (!Array.isArray(intervals) || !intervals.length) return [];

  const errors = new Array(intervals.length).fill(null);

  for (let i = 0; i < intervals.length; i++) {
    const err = validateInterval(intervals[i]);
    if (err) {
      errors[i] = err;
    }
  }

  // Check for overlapping intervals
  for (let i = 0; i < intervals.length; i++) {
    for (let j = i + 1; j < intervals.length; j++) {
      const a = intervals[i];
      const b = intervals[j];
      const aFrom = timeToMinutes(a?.from);
      const aTo = timeToMinutes(a?.to);
      const bFrom = timeToMinutes(b?.from);
      const bTo = timeToMinutes(b?.to);

      if (aFrom !== null && aTo !== null && bFrom !== null && bTo !== null && aFrom < aTo && bFrom < bTo) {
        if (Math.max(aFrom, bFrom) < Math.min(aTo, bTo)) {
          if (!errors[i]) errors[i] = "Time intervals cannot overlap";
          if (!errors[j]) errors[j] = "Time intervals cannot overlap";
        }
      }
    }
  }

  return errors;
};

/**
 * Validates entire opening hours schedule structure.
 */
export const isOpeningHoursValid = (openingHours, singleDayKey = null) => {
  if (!openingHours) return true;
  const normalized = normalizeOpeningHours(openingHours);

  if (singleDayKey) {
    const dayKey = singleDayKey.toUpperCase();
    const dayConfig = normalized.schedule[dayKey];
    if (dayConfig && dayConfig.active) {
      const errs = validateDayIntervals(dayConfig.intervals);
      if (errs.some(Boolean)) return false;
    }
    return true;
  }

  if (normalized.same_every_day) {
    const activeDays = WEEK_DAYS.filter((d) => normalized.schedule[d]?.active);
    if (activeDays.length > 0) {
      const refDay = activeDays[0];
      const errs = validateDayIntervals(normalized.schedule[refDay]?.intervals);
      if (errs.some(Boolean)) return false;
    }
    return true;
  }

  for (const day of WEEK_DAYS) {
    const dayConfig = normalized.schedule[day];
    if (dayConfig && dayConfig.active) {
      const errs = validateDayIntervals(dayConfig.intervals);
      if (errs.some(Boolean)) return false;
    }
  }

  return true;
};

/**
 * Formats intervals for display, e.g. "07:00 - 18:00"
 */
export const formatIntervals = (intervals, t = (s) => s) => {
  if (!Array.isArray(intervals) || !intervals.length) return t("Closed");
  return intervals.map((i) => `${i.from || "07:00"} — ${i.to || "18:00"}`).join(", ");
};

